import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { RefreshCw, Trash2 } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

export const InspectBankAllocationsDialog = ({
  open,
  onOpenChange,
  inspectBankId,
  inspectData,
  inspectLoading,
  fetchReconciliation,
  reconInvestorFilter,
  reconFromDate,
  reconToDate,
  forceCleanBank,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl" data-testid="inspect-allocations-dialog">
      <DialogHeader>
        <DialogTitle>Allocations on bank txn <span className="font-mono text-sm">{inspectBankId?.slice(0, 12)}…</span></DialogTitle>
        <DialogDescription>
          Diagnostic view of every allocation record pointing at this bank transaction. Use this when a bank looks stuck on
          "partially allocated" but you've already removed everything. Click <strong>Force-Clean</strong> at the bottom to
          wipe ALL allocation records on this bank and reset every recorded txn that thought it was matched.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {inspectLoading && <div className="p-6 text-center text-gray-500">Loading…</div>}
        {inspectData && (
          <>
            <div className="text-sm p-2 bg-slate-50 border rounded">
              <span className="font-semibold">Records found:</span> {inspectData.total_records}
            </div>
            {inspectData.allocation_records.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No allocation records on this bank. If the UI still shows partial-allocation, click <strong>Refresh Recon</strong> below.
              </div>
            ) : (
              inspectData.allocation_records.map((rec, i) => (
                <div key={i} className="border rounded p-3 space-y-2 bg-white" data-testid={`inspect-alloc-rec-${i}`}>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex gap-3 text-slate-600">
                      <span>Kind: <span className="font-mono">{rec.kind || '-'}</span></span>
                      <span>Status: <span className="font-mono">{rec.status || '-'}</span></span>
                      <span>Total: <span className="font-mono font-bold">{formatCurrency(rec.total_allocated)}</span></span>
                      {rec.write_off_amount > 0 && <span className="text-amber-700">Write-off: {formatCurrency(rec.write_off_amount)}</span>}
                    </div>
                    <span className="text-slate-400 text-[10px] font-mono">{rec.allocation_id?.slice(0, 8)}</span>
                  </div>
                  {(rec.details || []).map((d, j) => (
                    <div key={j} className={`text-xs p-2 rounded border flex items-center justify-between ${d.still_matched_on_recorded ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex-1">
                        <div className="font-mono text-[11px] truncate">
                          {d.transaction_id || '<no id>'}
                        </div>
                        <div className="text-slate-600 mt-0.5">
                          <span className="capitalize">{d.transaction_type || '-'}</span> · {d.investor_id || '-'} · {formatCurrency(d.allocated_amount)}
                        </div>
                      </div>
                      <div className="ml-2">
                        {d.still_matched_on_recorded ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Linked ✓</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-[10px]">Orphan ✗</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </>
        )}
      </div>
      <DialogFooter className="gap-2 flex-wrap">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        <Button
          variant="outline"
          onClick={() => fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate)}
          data-testid="inspect-refresh-recon"
        >
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh Recon
        </Button>
        <Button
          variant="destructive"
          onClick={forceCleanBank}
          disabled={inspectLoading}
          data-testid="inspect-force-clean"
        >
          <Trash2 className="h-4 w-4 mr-1" /> Force-Clean All
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
